import React, { useEffect, useState } from 'react';
import Card from '../components/common/Card';
import Loading from '../components/common/Loading';
import Alert from '../components/common/Alert';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import {
  DollarSign,
  TrendingUp,
  Users,
  Store,
  Calendar,
  Download,
  CheckCircle,
  Clock,
  XCircle,
} from 'lucide-react';
import { financialService } from '../services/financialService';
import { purchaseService } from '../services/purchaseService';
import { Purchase, FinancialStats } from '../types';
import { formatCurrency, formatDate, formatDateTime } from '../utils/formatters';

export const Financial: React.FC = () => {
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [stats, setStats] = useState<FinancialStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchFinancialData();
  }, [currentPage]);

  const fetchFinancialData = async () => {
    try {
      setLoading(true);
      const [purchasesRes, statsRes] = await Promise.all([
        purchaseService.getAll(currentPage),
        financialService.getStats(),
      ]);

      setPurchases(purchasesRes.data);
      setTotalPages(purchasesRes.last_page);
      setStats(statsRes);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message ||
                          err.response?.data?.error ||
                          'Erro ao carregar dados financeiros. Por favor, tente novamente.';
      setError(errorMessage);
      console.error('Financial data error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmado':
        return <Badge variant="success"><CheckCircle className="w-3 h-3 mr-1" />Confirmado</Badge>;
      case 'pendente':
        return <Badge variant="warning"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
      case 'cancelado':
        return <Badge variant="danger"><XCircle className="w-3 h-3 mr-1" />Cancelado</Badge>;
      default:
        return <Badge variant="info">{status}</Badge>;
    }
  };

  const handleExport = async () => {
    try {
      const blob = await financialService.exportReport(undefined, 'pdf');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio-financeiro-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      setError('Erro ao exportar relatório');
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Financeiro</h1>
            <p className="text-gray-600 mt-2">
              Acompanhe as utilizações e estatísticas do sistema de benefícios
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleExport}
            className="flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            Exportar Relatório
          </Button>
        </div>

        {/* Alerts */}
        {error && (
          <Alert type="error" className="mb-6" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="hover:shadow-lg transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">
                    Total de Utilizações
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats?.total_utilizacoes || 0}
                  </p>
                </div>
                <div className="bg-blue-500 p-3 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">
                    Desconto Total
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(stats?.desconto_total || 0)}
                  </p>
                </div>
                <div className="bg-green-500 p-3 rounded-lg">
                  <DollarSign className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">
                    Utilizações Confirmadas
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats?.utilizacoes_confirmadas || 0}
                  </p>
                </div>
                <div className="bg-purple-500 p-3 rounded-lg">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">
                    Associados Ativos
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats?.associados_ativos || 0}
                  </p>
                </div>
                <div className="bg-orange-500 p-3 rounded-lg">
                  <Users className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Stats adicionais */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <div className="p-6">
              <p className="text-sm font-medium text-gray-600 mb-2">Valor Total Confirmado</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(stats?.valor_total || 0)}
              </p>
            </div>
          </Card>
          <Card>
            <div className="p-6">
              <p className="text-sm font-medium text-gray-600 mb-2">Desconto Médio</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.desconto_medio?.toFixed(2) || '0.00'}%
              </p>
            </div>
          </Card>
          <Card>
            <div className="p-6">
              <p className="text-sm font-medium text-gray-600 mb-2">Utilizações Pendentes</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.utilizacoes_pendentes || 0}
              </p>
            </div>
          </Card>
        </div>

        {/* Transactions Table */}
        <Card>
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Utilizações Recentes
            </h2>

            {purchases.length === 0 ? (
              <div className="text-center py-12">
                <TrendingUp className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Nenhuma utilização encontrada
                </h3>
                <p className="text-gray-600">
                  As utilizações de benefícios aparecerão aqui
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data/Hora
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Associado
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Parceiro
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Desconto
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Valor Original
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Valor Final
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {purchases.map((purchase) => (
                        <tr key={purchase.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDateTime(purchase.data_utilizacao || purchase.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {purchase.associado?.nome || purchase.associado?.name || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {purchase.parceiro?.nome_fantasia || purchase.parceiro?.name || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                            {purchase.desconto_aplicado}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {purchase.valor_original ? formatCurrency(purchase.valor_original) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                            {purchase.valor_final ? formatCurrency(purchase.valor_final) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {getStatusBadge(purchase.status)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-6 pt-6 border-t border-gray-200">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      size="sm"
                    >
                      Anterior
                    </Button>
                    <span className="px-4 py-2 text-sm text-gray-700">
                      Página {currentPage} de {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      size="sm"
                    >
                      Próxima
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
